import { Button, Modal, Select, Stack, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { disputesApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { DISPUTE_TYPE_LABELS } from "../lib/format";
import type { DisputeType } from "../types/api";

const TYPE_OPTIONS = Object.entries(DISPUTE_TYPE_LABELS).map(([value, label]) => ({ value, label }));

interface Props {
  opened: boolean;
  onClose: () => void;
  submissionId?: string;
  placementId?: string;
  defaultType?: DisputeType;
  onCreated?: (id: string) => void;
}

export function RaiseDisputeModal({
  opened,
  onClose,
  submissionId,
  placementId,
  defaultType = "other",
  onCreated,
}: Props) {
  const queryClient = useQueryClient();
  const [disputeType, setDisputeType] = useState<string>(defaultType);
  const [description, setDescription] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      disputesApi.create({
        dispute_type: disputeType as DisputeType,
        description,
        submission_id: submissionId,
        placement_id: placementId,
      }),
    onSuccess: (dispute) => {
      notifications.show({
        color: "green",
        title: "Dispute submitted",
        message: "AxioHire will review and mediate.",
      });
      queryClient.invalidateQueries({ queryKey: ["disputes"] });
      setDescription("");
      onClose();
      onCreated?.(dispute.id);
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Could not raise dispute";
      notifications.show({ color: "red", title: "Failed", message });
    },
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Raise a dispute" centered>
      <Stack>
        <Select
          label="Dispute type"
          data={TYPE_OPTIONS}
          value={disputeType}
          onChange={(v) => v && setDisputeType(v)}
        />
        <Textarea
          label="Describe the issue"
          description="Include facts AxioHire can verify (dates, what happened)."
          minRows={4}
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          required
        />
        <Button
          color="violet"
          loading={mutation.isPending}
          disabled={description.trim().length < 10}
          onClick={() => mutation.mutate()}
        >
          Submit dispute
        </Button>
      </Stack>
    </Modal>
  );
}
